import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MeetingService } from '../../../../../services/meeting.service';
import { UserService } from '../../../../../services/user.service';
import { Meeting } from '../../../../../models/meeting';
import { injectFocusTarget } from '../../../../../helpers/focus-target';
import {
  DenyMeetingDialogComponent,
  DenyMeetingDialogResult,
} from '../../../../common/deny-meeting-dialog/deny-meeting-dialog';
import {
  ProposeChangesDialogComponent,
  ProposeChangesDialogResult,
} from '../../../../common/propose-changes-dialog/propose-changes-dialog';

/**
 * Counselor-facing list of everything still touching their pending queue. This
 * includes new requests and proposals in either direction. A proposal the counselor
 * made themselves stays visible here too, just without action buttons; see `isAwaitingMe`.
 */
@Component({
  selector: 'app-counselor-meetings-pending',
  imports: [MatIconModule, DatePipe, MatButtonModule],
  templateUrl: './counselor-meetings-pending.html',
  styleUrl: './counselor-meetings-pending.css',
})
export class CounselorMeetingsPending implements OnInit {
  pendingRequests: Meeting[] = [];

  /** Ids currently mid-flight on confirm/deny, so their buttons can be disabled. */
  processingIds = new Set<number>();

  /** Card a notification pointed at, highlighted so it stands out on arrival. */
  focusedId: number | null = null;

  private meetingService = inject(MeetingService);
  private userService = inject(UserService);
  private cdf = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private host = inject(ElementRef<HTMLElement>);

  /**
   * Scrolls to the request a notification pointed at and highlights it. This tab
   * has no details dialog meaning the actions are the buttons on the card. Bringing
   * the card into view is the whole job.
   */
  private focusTarget = injectFocusTarget<Meeting>({
    param: 'meetingId',
    items: () => this.pendingRequests,
    onFocus: (meeting) => {
      this.focusedId = meeting.id;
      this.cdf.detectChanges();
      // Optional call: scrollIntoView is absent in non-browser DOMs.
      this.host.nativeElement
        .querySelector(`[data-testid="request-card-${meeting.id}"]`)
        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    },
  });

  ngOnInit(): void {
    this.loadPendingRequests();

    this.meetingService.meetingsChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadPendingRequests());
  }

  /**
   * Requests still worth a decision. A lapsed one is dropped: the student
   * re-requests rather than the counselor reviving it, and it would otherwise
   * sit here forever.
   */
  private loadPendingRequests(): void {
    this.meetingService.getMeetings().subscribe({
      next: (meetings) => {
        console.log(meetings);
        this.pendingRequests = meetings.filter(
          (m) => (m.status === 'pending' && !m.isLapsed) || m.hasLiveProposal,
        );
        this.cdf.detectChanges();
        this.focusTarget.check();
      },
    });
  }

  /** Whether it's THIS counselor's move on the given card. Drives which
   *  action buttons show versus a read only "awaiting student" state. */
  isAwaitingMe(meeting: Meeting): boolean {
    return meeting.awaitingResponseUserId === this.userService.currentUser?.id;
  }

  confirm(meeting: Meeting): void {
    if (this.processingIds.has(meeting.id)) return;
    this.processingIds.add(meeting.id);
    this.meetingService.confirmMeeting(meeting.id).subscribe({
      next: () => this.loadPendingRequests(),
      error: () => this.processingIds.delete(meeting.id),
    });
  }

  deny(meeting: Meeting): void {
    if (this.processingIds.has(meeting.id)) return;
    this.dialog
      .open(DenyMeetingDialogComponent, {
        data: { meeting },
        width: '480px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: DenyMeetingDialogResult) => {
        if (!result) return; // counselor backed out of the dialog
        this.processingIds.add(meeting.id);
        this.meetingService.denyMeeting(meeting.id, result.note).subscribe({
          next: () => this.loadPendingRequests(),
          error: () => this.processingIds.delete(meeting.id),
        });
      });
  }

  proposeChanges(meeting: Meeting): void {
    if (this.processingIds.has(meeting.id)) return;
    this.dialog
      .open(ProposeChangesDialogComponent, {
        data: { meeting },
        width: '720px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: ProposeChangesDialogResult) => {
        if (!result) return;
        this.loadPendingRequests();
      });
  }
}
